import React, { FC, useEffect, useState } from "react";
import { Box, Typography, Button, useTheme, Slide } from "@mui/material";
import TennisTable from "./TennisTables";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import TablePlayers from "./TablePlayers";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  fetchTables,
  getAvailableRooms,
  getAvailableRoomNames,
  getTablesWithMatchsGroupedByRows,
  listenToMatchs,
} from "../../features/matchs/matchsSlice";
import {
  fetchTournoi,
  getLiveRefreshPeriodInSeconds,
} from "../../features/tournoi/tournoiSlice";

interface LiveProps {}

const Live: FC<LiveProps> = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [roomIdx, setRoomIdx] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const availableRooms = useAppSelector(getAvailableRooms);
  const availableRoomNames = useAppSelector(getAvailableRoomNames);
  const tablesByRow = useAppSelector((state: RootState) =>
    getTablesWithMatchsGroupedByRows(state, availableRooms[roomIdx] ?? 1)
  );

  const refreshInterval = useAppSelector(getLiveRefreshPeriodInSeconds);

  useEffect(() => {
    console.log("🔍 [DEBUG] Live useEffect - début");
    try {
      console.log("🔍 [DEBUG] Appel de fetchTables");
      dispatch(fetchTables());
      console.log("🔍 [DEBUG] Appel de listenToMatchs");
      const unsubscribe = dispatch(listenToMatchs());
      console.log("🔍 [DEBUG] Live useEffect - succès");
      return () => {
        console.log("🔍 [DEBUG] Live useEffect - cleanup");
        unsubscribe();
      };
    } catch (error) {
      console.error("❌ [DEBUG] Erreur dans Live useEffect:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (roomIdx < availableRoomNames.length - 1) {
        setRoomIdx((prevIndex) => prevIndex + 1);
      } else {
        setRoomIdx(0);
        dispatch(fetchTournoi());
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [roomIdx, availableRoomNames, dispatch, refreshInterval]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      setIsFullScreen(fullscreenElement != null);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange);
    document.addEventListener("mozfullscreenchange", handleFullScreenChange);
    document.addEventListener("MSFullscreenChange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullScreenChange
      );
    };
  }, []);

  const handleFullScreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if ((el as any).webkitRequestFullscreen)
      (el as any).webkitRequestFullscreen();
    else if ((el as any).mozRequestFullScreen)
      (el as any).mozRequestFullScreen();
    else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();
  };

  return (
    tablesByRow && (
      <Slide in direction="left" key={roomIdx} timeout={500}>
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            height: "100vh",
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: isFullScreen ? "center" : "flex-start",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 10,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: "bold",
              boxShadow: 2,
              textAlign: "center",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {availableRoomNames[roomIdx] || `Salle ${availableRooms[roomIdx]}`}
          </Typography>

          {!isFullScreen && (
            <Button
              variant="contained"
              onClick={handleFullScreen}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
              }}
            >
              Plein écran
            </Button>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              width: "100%",
              maxWidth: isFullScreen ? "100%" : "1200px",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Grid2
              container
              direction="column"
              sx={{
                flexGrow: 1,
                justifyContent: "space-evenly",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              {Object.keys(tablesByRow).map((row) => (
                <Grid2
                  container
                  key={row}
                  sx={{
                    flexGrow: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {tablesByRow[parseInt(row)].map((table, idx) => (
                    <Grid2 key={idx} xs>
                      <TennisTable tableId={table.tableId!}>
                        {table.matchs![0]?.matchsPlayerParticipations && (
                          <TablePlayers
                            matchPlayers={
                              table.matchs![0].matchsPlayerParticipations
                            }
                          />
                        )}
                      </TennisTable>
                    </Grid2>
                  ))}
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Box>
      </Slide>
    )
  );
};

export default Live;
